import React, { useState, useEffect } from 'react';
import { api } from '../../App';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { AlertTriangle, Loader2, Info } from 'lucide-react';

const useImpact = (txn) => {
  const [impact, setImpact] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!txn) { setImpact(null); return; }
    setLoading(true);
    api.get(`/transactions/${txn.id}/impact`)
      .then(res => setImpact(res.data))
      .catch(err => setImpact({ can_modify: false, blocked_reason: err.response?.data?.detail || 'Failed to load impact analysis' }))
      .finally(() => setLoading(false));
  }, [txn]);
  return { impact, loading };
};

const ImpactPanel = ({ impact, loading }) => {
  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 p-3 bg-gray-50 rounded-lg">
        <Loader2 size={14} className="animate-spin" /> Analyzing impact...
      </div>
    );
  }
  if (!impact) return null;
  if (!impact.can_modify) {
    return (
      <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex gap-2" data-testid="impact-blocked">
        <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
        <span>{impact.blocked_reason}</span>
      </div>
    );
  }
  return (
    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm space-y-1" data-testid="impact-effects">
      <p className="font-semibold text-blue-800 flex items-center gap-1"><Info size={14} /> What this will do:</p>
      {impact.effects?.map((e, i) => (
        <p key={i} className="text-blue-700 ml-5">• {e}</p>
      ))}
      {impact.warnings?.map((w, i) => (
        <p key={`w-${i}`} className="text-amber-700 ml-5">⚠ {w}</p>
      ))}
    </div>
  );
};

export const DeleteTransactionDialog = ({ txn, onClose, onSuccess }) => {
  const { impact, loading } = useImpact(txn);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await api.delete(`/transactions/${txn.id}`);
      toast.success('Transaction deleted and portfolio effects reversed');
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete transaction');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={!!txn} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="delete-transaction-dialog">
        <DialogHeader>
          <DialogTitle>Delete Transaction</DialogTitle>
        </DialogHeader>
        {txn && (
          <div className="space-y-4">
            <div className="text-sm bg-gray-50 p-3 rounded-lg">
              <span className={`font-semibold ${txn.transaction_type === 'sell' ? 'text-red-600' : 'text-green-700'}`}>
                {txn.transaction_type.toUpperCase()}
              </span>{' '}
              {Number(txn.quantity).toLocaleString('en-IN')} × <strong>{txn.symbol}</strong> @ ₹{Number(txn.price).toFixed(2)}
              <span className="text-gray-500"> · {new Date(txn.transaction_date).toLocaleDateString('en-IN')}</span>
            </div>
            <ImpactPanel impact={impact} loading={loading} />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={onClose} data-testid="delete-cancel-btn">Cancel</Button>
              <Button
                onClick={handleDelete}
                disabled={loading || deleting || !impact?.can_modify}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="delete-confirm-btn"
              >
                {deleting ? 'Deleting...' : 'Delete & Reverse'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const EditTransactionDialog = ({ txn, members, onClose, onSuccess }) => {
  const { impact, loading } = useImpact(txn);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ member_id: '', symbol: '', quantity: '', price: '', transaction_date: '' });

  useEffect(() => {
    if (txn) {
      setForm({
        member_id: txn.member_id,
        symbol: txn.symbol,
        quantity: txn.quantity,
        price: txn.price,
        transaction_date: String(txn.transaction_date).split('T')[0],
      });
    }
  }, [txn]);

  const handleSave = async () => {
    if (!form.symbol || !form.quantity || !form.price) {
      toast.error('Symbol, quantity and price are required');
      return;
    }
    try {
      setSaving(true);
      const res = await api.put(`/transactions/${txn.id}`, {
        member_id: form.member_id,
        symbol: form.symbol,
        quantity: Number(form.quantity),
        price: Number(form.price),
        transaction_date: form.transaction_date,
      });
      const msg = res.data?.result?.message || 'Transaction updated';
      toast.success(msg);
      if (res.data?.result?.warning) toast.warning(res.data.result.warning);
      onSuccess();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update transaction');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!txn} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="edit-transaction-dialog">
        <DialogHeader>
          <DialogTitle>Edit Transaction ({txn?.transaction_type?.toUpperCase()})</DialogTitle>
        </DialogHeader>
        {txn && (
          <div className="space-y-4">
            <ImpactPanel impact={impact} loading={loading} />
            {impact?.can_modify && (
              <>
                <p className="text-xs text-gray-500">
                  Saving will reverse the original portfolio effect and re-apply with the corrected values below.
                </p>
                <div>
                  <label className="block text-sm font-medium mb-1">Member</label>
                  <select
                    className="input-field w-full"
                    value={form.member_id}
                    onChange={(e) => setForm({ ...form, member_id: e.target.value })}
                    data-testid="edit-member-select"
                  >
                    {members.map((m) => (
                      <option key={m.id} value={m.id}>{m.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Symbol</label>
                  <input
                    type="text"
                    className="input-field w-full"
                    value={form.symbol}
                    onChange={(e) => setForm({ ...form, symbol: e.target.value.toUpperCase() })}
                    data-testid="edit-symbol-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium mb-1">Quantity</label>
                    <input
                      type="number"
                      className="input-field w-full"
                      value={form.quantity}
                      onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                      data-testid="edit-quantity-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">Price (₹)</label>
                    <input
                      type="number"
                      step="0.01"
                      className="input-field w-full"
                      value={form.price}
                      onChange={(e) => setForm({ ...form, price: e.target.value })}
                      data-testid="edit-price-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Date</label>
                  <input
                    type="date"
                    className="input-field w-full"
                    value={form.transaction_date}
                    onChange={(e) => setForm({ ...form, transaction_date: e.target.value })}
                    data-testid="edit-date-input"
                  />
                </div>
              </>
            )}
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={onClose} data-testid="edit-cancel-btn">Cancel</Button>
              <Button
                onClick={handleSave}
                disabled={loading || saving || !impact?.can_modify}
                className="btn-primary"
                data-testid="edit-save-btn"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
